<div class="config-container">
  <header class="config-header">
    <h1>CSV→JSON 設定</h1>
  </header>

  @if (executeError()) {
    <div class="error-banner">{{ executeError() }}</div>
  }

  <form [formGroup]="configForm" (ngSubmit)="onSubmit()" class="glass-form">
    <section class="form-section">
      <div class="section-title">
        <span class="icon">📝</span>
        <h2>基本情報</h2>
      </div>

      <div class="grid-fields">
        <div class="form-group">
          <label for="wbsTaskId">
            wbsTaskId (クエリ)
            <span class="field-hint">Wbsタスクの構成要素として識別するID</span>
          </label>
          <div class="inline-with-btn">
            <input id="wbsTaskId" type="text" formControlName="wbsTaskId" />
            <button type="button" class="submit-btn" (click)="fetchSavedConfig()" [disabled]="loading()">
              {{ loading() ? '...' : '読込' }}
            </button>
          </div>
        </div>

        <div class="form-group">
          <label for="id">
            タスクID
            <span class="field-hint">実行時に使用する個別のタスクID</span>
          </label>
          <input id="id" type="text" formControlName="id" />
        </div>

        <div class="form-group span-two">
          <label for="name">
            タスク名称
            <span class="field-hint">タスクの用途を説明する名称</span>
          </label>
          <input id="name" type="text" formControlName="name" />
        </div>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">🎯</span>
        <h2>コンバーター対象</h2>
      </div>

      <div class="grid-fields">
        <div class="form-group">
          <label for="searchPath">
            CSV 問い合わせ先パス
            <span class="field-hint">リソースを取得するURLやパスの定義</span>
          </label>
          <div class="input-with-upload">
            <input id="searchPath" type="text" formControlName="searchPath" />
            <app-file-transfer
              [initialFileId]="configForm.get('searchPath')?.value || ''"
              [compact]="true"
            ></app-file-transfer>
          </div>
        </div>

        <div class="form-group">
          <label for="httpMethod">
            HTTPメソッド
            <span class="field-hint">API呼び出し時の接続方式（GET/POST）</span>
          </label>
          <select id="httpMethod" formControlName="httpMethod">
            <option value="get">get</option>
            <option value="post">post</option>
          </select>
        </div>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">📊</span>
        <h2>エンティティスキーマ</h2>
        <span class="section-hint">CSVファイルのレイアウト（項目やデータ型）を定義します</span>
      </div>

      <div class="grid-fields compact">
        <div class="form-group">
          <label for="contentsType">
            コンテンツタイプ
            <span class="field-hint">ファイル形式（CSV/固定長など）</span>
          </label>
          <input id="contentsType" type="text" formControlName="contentsType" readonly />
        </div>

        <div class="form-group">
          <label for="headerCount">
            ヘッダー行数
            <span class="field-hint">ファイルの先頭にある読み飛ばす行数</span>
          </label>
          <input id="headerCount" type="number" formControlName="headerCount" min="0" />
        </div>
      </div>

      <div formArrayName="fields" class="fields-list">
        @for (field of fields.controls; track field.get('_id')?.value) {
          <div [formGroupName]="$index" class="field-item">
            <div class="reorder-actions">
              <button type="button" class="order-btn" (click)="moveFieldUp($index)" [disabled]="$first">▲</button>
              <button type="button" class="order-btn" (click)="moveFieldDown($index)" [disabled]="$last">▼</button>
            </div>

            <div class="field-inputs">
              <div class="form-group mini">
                <label>項目名</label>
                <input type="text" formControlName="fieldName" placeholder="name" />
              </div>
              <div class="form-group mini">
                <label>項目型</label>
                <select formControlName="fieldType">
                  @for (option of fieldTypeOptions; track option) {
                    <option [value]="option">{{ option }}</option>
                  }
                </select>
              </div>
            </div>

            <button type="button" class="remove-btn" (click)="removeField($index)">×</button>
          </div>
        }
      </div>

      <div class="actions-row">
        <button type="button" class="add-btn" (click)="addField()">+ 項目を追加</button>
        <button type="button" class="clear-btn" (click)="clearFields()">
          <span class="icon">🗑️</span> フィールドを全て削除
        </button>
        <button type="button" class="analyze-btn" (click)="analyzeCsvHeaders()" [disabled]="analyzing()">
          @if (analyzing()) {
            <span class="btn-loader"></span> 解析中...
          } @else {
            <span class="icon">🔍</span> CSVから項目を読込
          }
        </button>
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">💾</span>
        <h2>保存先データ</h2>
      </div>
      <div class="form-group">
        <label for="savePath">
          保存先パス
          <span class="field-hint">保存対象のファイルシステムやAPIのパス</span>
        </label>
        <input id="savePath" type="text" formControlName="savePath" />
      </div>
    </section>

    <section class="form-section">
      <div class="section-title">
        <span class="icon">🧩</span>
        <h2>View Schema</h2>
        <span class="section-hint">表示変換定義（任意）</span>
      </div>

      <app-view-schema-editor formControlName="viewSchemaJson" mode="indexApp"></app-view-schema-editor>
    </section>

    <footer class="form-footer">
      <button type="button" class="execute-btn" (click)="executeTask()" [disabled]="isExecuting()">
        @if (isExecuting()) {
          <span class="btn-loader"></span>実行中...
        } @else {
          ▶ 変換実行
        }
      </button>
      <button type="submit" [disabled]="configForm.invalid || saving()" class="submit-btn">
        @if (saving()) {
          <span class="btn-loader"></span>
          保存中...
        } @else {
          CSV設定を保存
        }
      </button>
    </footer>
  </form>

  @if (hasExecuted()) {
    <section class="results-section glass-form">
      <div class="section-title">
        <span class="icon">✅</span>
        <h2>実行結果 ({{ configForm.get('savePath')?.value }})</h2>
      </div>
      @if (isLoadingResult()) {
        <div class="results-loading">結果を読み込んでいます...</div>
      } @else if (resultRows().length > 0) {
        <div class="results-header">
          <div class="results-meta">
            <span class="count-badge">{{ resultRows().length }} 件</span>
            <div class="view-switch">
              <button
                type="button"
                class="switch-btn"
                [class.active]="viewMode() === 'table'"
                (click)="viewMode.set('table')"
              >
                テーブル表示
              </button>
              <button
                type="button"
                class="switch-btn"
                [class.active]="viewMode() === 'raw'"
                (click)="viewMode.set('raw')"
              >
                JSON表示
              </button>
            </div>
          </div>
        </div>
        @if (viewMode() === 'table') {
          <div class="table-container glass-card">
            <table class="premium-table">
              <thead>
                <tr>
                  @for (col of getColumns(); track col) {
                    <th>{{ translateColumn(col) }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of resultRows(); track $index) {
                  <tr>
                    @for (col of getColumns(); track col) {
                      <td>{{ row[col] }}</td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="raw-container glass-card">
            <pre>{{ getRawJson() }}</pre>
          </div>
        }
      } @else if (!executeError()) {
        <div class="empty-state glass-card">
          <p>変換タスクからデータが返されませんでした。</p>
        </div>
      }
    </section>
  }
</div>
